import React from "react"
import { Prompt } from "react-router"
import connectPreview from "./preview/PreviewController"
import AppearancePreview from "./preview/AppearancePreview"
import { LAUNCHER_TYPES } from "../../app/constants"
import {
  Switch,
  Button,
  Fieldset,
  TextArea,
  TextInput,
  RadioGroup,
  CreateForm,
  RadioButton,
  ColorPicker,
  SubmitButton
} from "../../lib"

const schema = {
  brand_color: {
    label: "Brand color",
    placeholder: "#2C2C46",
    defaultValue: "#2C2C46"
  },
  text_color: {
    label: "Text color",
    placeholder: "#FFFFFF",
    defaultValue: "#FFFFFF"
  },
  launcher_type: {
    label: "Launcher type",
    defaultValue: "button"
  },
  launcher_pos: {
    label: "Launcher position",
    defaultValue: "right"
  },
  launcher_prompt: {
    label: "Launcher prompt",
    placeholder: "Send a message",
    defaultValue: "Send a message"
  },
  messenger_prompt: {
    label: "Messenger prompt",
    placeholder: "How can we help you?",
    defaultValue: "How can we help you?"
  },
  messenger_entry: {
    label: "Messenger entry",
    placeholder: "Send a message...",
    defaultValue: "Send a message..."
  },
  messenger_blank: {
    label: "No messages prompt",
    placeholder: "Send a message, and we’ll reply as soon as we can.",
    defaultValue: "Send a message, and we’ll reply as soon as we can."
  },
  hide_team_icon: {
    label: "Hide team icon",
    defaultValue: false
  },
  hide_logo: {
    label: 'Hide "powered by smallchat" link',
    defaultValue: false
  },
  contact_enabled: {
    label: "Collect user name and contact info",
    defaultValue: true
  },
  contact_immediately: {
    defaultValue: false
  },
  contact_dismissible: {
    defaultValue: false
  },
  contact_prompt: {
    label: "Name and contact prompt",
    placeholder: "Add your name and email to make sure you see our reply:",
    defaultValue: "Add your name and email to make sure you see our reply:",
    rows: 3
  },
  contact_reply: {
    label: "Contact reply",
    placeholder:
      "Thanks {{name}}! You'll get a response here or we'll contact you at {{contact}}.",
    defaultValue:
      "Thanks {{name}}! You'll get a response here or we'll contact you at {{contact}}.",
    rows: 4
  },
  name_field_label: {
    label: "Name field label",
    placeholder: "Name",
    defaultValue: "Name"
  },
  contact_field_label: {
    label: "Contact field label",
    placeholder: "Email",
    defaultValue: "Email"
  },
  offline_greeting: {
    label: "Offline greeting",
    placeholder:
      "We’re offline right now but typically respond in about an hr.",
    defaultValue:
      "We’re offline right now but typically respond in about an hr.",
    rows: 3
  },
  custom_css: {
    label: "Custom CSS",
    placeholder: "img { opacity: .5; }",
    defaultValue: "",
    rows: 3
  }
}

const AppearanceForm = connectPreview(
  ({
    preview,
    disabled,
    bindPreview,
    hasWhiteLabelAccess,
    fieldsData,
    handleSubmit,
    isPristine,
    ...rest
  }) => {
    return (
      <form className="AppearanceForm" onSubmit={handleSubmit}>
        <Prompt
          when={!isPristine && !disabled}
          message="You have unsaved changes. Are you sure you want to leave?"
        />

        <AppearancePreview {...fieldsData} display={preview} />

        <Fieldset label="Theme" onMouseOver={bindPreview("messenger")}>
          <ColorPicker {...fieldsData.brand_color} />
          <ColorPicker {...fieldsData.text_color} />
        </Fieldset>

        <Fieldset label="Custom CSS" onMouseOver={bindPreview("custom_css")}>
          <TextArea {...fieldsData.custom_css} />
        </Fieldset>
        <Fieldset label="Launcher" onMouseOver={bindPreview("launcher")}>
          <RadioGroup {...fieldsData.launcher_type}>
            <RadioButton value={LAUNCHER_TYPES.BUTTON}>Button</RadioButton>
            <RadioButton value={LAUNCHER_TYPES.ICON}>Icon</RadioButton>
            <RadioButton value={LAUNCHER_TYPES.TAB}>Tab</RadioButton>
          </RadioGroup>
          <RadioGroup {...fieldsData.launcher_pos}>
            <RadioButton value="right">Right</RadioButton>
            <RadioButton value="left">Left</RadioButton>
          </RadioGroup>
          {fieldsData.launcher_type.value !== "icon" && (
            <TextInput {...fieldsData.launcher_prompt} />
          )}
        </Fieldset>

        <Fieldset label="Messenger" onMouseOver={bindPreview("messenger")}>
          <TextInput {...fieldsData.messenger_prompt} />
          <TextInput {...fieldsData.messenger_entry} />
          <TextArea {...fieldsData.messenger_blank} />
          <Switch {...fieldsData.hide_team_icon} />
          <Switch
            {...fieldsData.hide_logo}
            disabled={!hasWhiteLabelAccess}
            status={
              !hasWhiteLabelAccess && (
                <p>
                  To remove Smallchat branding
                  <br />
                  you'll need to&nbsp;
                  <Button styleType="text" to="/account/billing">
                    upgrade your plan
                  </Button>.
                </p>
              )
            }
          />
        </Fieldset>

        <Fieldset label="Offline Mode" onMouseOver={bindPreview("offline")}>
          <TextArea {...fieldsData.offline_greeting} />
        </Fieldset>

        <Fieldset label="User Data" onMouseOver={bindPreview("contact")}>
          <Switch {...fieldsData.contact_enabled} />
          {fieldsData.contact_enabled.value && (
            <TextArea {...fieldsData.contact_prompt} />
          )}
          {fieldsData.contact_enabled.value && (
            <TextInput {...fieldsData.name_field_label} />
          )}
          {fieldsData.contact_enabled.value && (
            <TextInput {...fieldsData.contact_field_label} />
          )}
          {fieldsData.contact_enabled.value && (
            <RadioGroup {...fieldsData.contact_immediately}>
              <RadioButton value={false}>
                Show form after user sends a message
              </RadioButton>
              <RadioButton value={true}>
                Show form when user opens chat
              </RadioButton>
            </RadioGroup>
          )}
          {fieldsData.contact_enabled.value && (
            <RadioGroup {...fieldsData.contact_dismissible}>
              <RadioButton value={false}>Require contact info</RadioButton>
              <RadioButton value={true}>Let user dismiss the form</RadioButton>
            </RadioGroup>
          )}
        </Fieldset>

        {fieldsData.contact_enabled.value && (
          <Fieldset label="Reply" onMouseOver={bindPreview("reply")}>
            <TextArea {...fieldsData.contact_reply} />
          </Fieldset>
        )}

        <footer>
          <SubmitButton
            className={(isPristine || disabled) && "SubmitButton-hidden"}
            {...rest}
          >
            Save
          </SubmitButton>
        </footer>
      </form>
    )
  }
)

AppearanceForm.displayName = "AppearanceForm"

export default CreateForm(AppearanceForm, schema)



// WEBPACK FOOTER //
// ./src/channel/appearance/AppearanceForm.js